import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import Rooms from "@/pages/Rooms";
import Reservations from "@/pages/Reservations";
import Guests from "@/pages/Guests";
import Housekeeping from "@/pages/Housekeeping";
import Invoices from "@/pages/Invoices";
import InvoicePrint from "@/pages/InvoicePrint";
import VoucherPrint from "@/pages/VoucherPrint";
import HallBookingPrint from "@/pages/halls/HallBookingPrint";
import AmenityBookingPrint from "@/pages/halls/AmenityBookingPrint";
import { KotPrint, ReceiptPrint } from "@/pages/pos/PosPrint";
import Timeline from "@/pages/Timeline";
import RateCalendar from "@/pages/RateCalendar";
import Channels from "@/pages/Channels";
import AdminConsole from "@/pages/AdminConsole";
import GstReportPrint from "@/pages/GstReportPrint";
import Staff from "@/pages/Staff";
import Audit from "@/pages/Audit";
import PublicMenu from "@/pages/PublicMenu";
import Reports from "@/pages/Reports";
import Halls from "@/pages/Halls";
import Amenities from "@/pages/Amenities";
import SettingsPage from "@/pages/Settings";
import Attendance from "@/pages/Attendance";
import Payroll from "@/pages/Payroll";
import Expenses from "@/pages/Expenses";
import PublicBook from "@/pages/PublicBook";
import Help from "@/pages/Help";
import Quotations from "@/pages/Quotations";
import MyTasks from "@/pages/MyTasks";
import { RequireSection } from "@/lib/permissions";
import POS from "@/pages/POS";

function App() {
  useEffect(() => {
    // Register service worker for offline app-shell + API GET caching.
    // Auto-reload the page once a new worker takes over so users always land on
    // the latest build without needing to hard-refresh.
    if ("serviceWorker" in navigator) {
      let reloading = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });
      navigator.serviceWorker.register("/sw.js").then((reg) => {
        reg.update?.().catch(() => {});
        reg.addEventListener?.("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            // A new worker is installed while an old one still controls the page.
            // Ask it to take over immediately — controllerchange handler above will reload.
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              nw.postMessage("SKIP_WAITING");
            }
          });
        });
      }).catch(() => {});
    }
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/book/:pid" element={<PublicBook />} />
            <Route path="/menu/:pid" element={<PublicMenu />} />
            <Route path="/invoices/:id/print" element={<InvoicePrint />} />
            <Route path="/reservations/:id/voucher" element={<VoucherPrint />} />
            <Route path="/halls/bookings/:id/print" element={<HallBookingPrint />} />
            <Route path="/amenities/bookings/:id/print" element={<AmenityBookingPrint />} />
            <Route path="/reports/gst/print" element={<GstReportPrint />} />
            <Route path="/pos/print/kot/:orderId" element={<KotPrint />} />
            <Route path="/pos/print/receipt/:entityId" element={<ReceiptPrint />} />
            <Route element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<RequireSection section="dashboard"><Dashboard /></RequireSection>} />
              <Route path="/timeline" element={<RequireSection section="front_desk"><Timeline /></RequireSection>} />
              <Route path="/rates" element={<RequireSection section="rate_calendar"><RateCalendar /></RequireSection>} />
              <Route path="/channels" element={<RequireSection section="channels"><Channels /></RequireSection>} />
              <Route path="/properties" element={<RequireSection section="properties"><Properties /></RequireSection>} />
              <Route path="/rooms" element={<RequireSection section="rooms"><Rooms /></RequireSection>} />
              <Route path="/reservations" element={<RequireSection section="reservations"><Reservations /></RequireSection>} />
              <Route path="/guests" element={<RequireSection section="guests"><Guests /></RequireSection>} />
              <Route path="/housekeeping" element={<RequireSection section="housekeeping"><Housekeeping /></RequireSection>} />
              <Route path="/invoices" element={<RequireSection section="invoices"><Invoices /></RequireSection>} />
              <Route path="/staff" element={<RequireSection section="staff"><Staff /></RequireSection>} />
              <Route path="/audit" element={<RequireSection section="audit"><Audit /></RequireSection>} />
              <Route path="/attendance" element={<RequireSection section="attendance"><Attendance /></RequireSection>} />
              <Route path="/payroll" element={<RequireSection section="payroll"><Payroll /></RequireSection>} />
              <Route path="/expenses" element={<RequireSection section="expenses"><Expenses /></RequireSection>} />
              <Route path="/pos" element={<RequireSection section="pos"><POS /></RequireSection>} />
              <Route path="/halls" element={<RequireSection section="halls"><Halls /></RequireSection>} />
              <Route path="/amenities" element={<RequireSection section="amenities"><Amenities /></RequireSection>} />
              <Route path="/reports" element={<RequireSection section="reports"><Reports /></RequireSection>} />
              <Route path="/quotations" element={<RequireSection section="invoices"><Quotations /></RequireSection>} />
              <Route path="/my-tasks" element={<RequireSection section="housekeeping"><MyTasks /></RequireSection>} />
              <Route path="/help" element={<Help />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin" element={<RequireSection section="admin"><AdminConsole /></RequireSection>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
